/* External */
import {
  memo, useEffect, useCallback, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Title from '@dtvgo/title';
import CodeError from '@dtvgo/code-error';

/* Assets */
import Icon from '~/assets/theme/svg/icon-profile-error.svg';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* Utils */
import { APP_VERSION, DATADOG_APPLICATION_ID } from '../../../../utils/env';
import { TYPE_ACTIONS_PROFILE } from '../../../../utils/constants';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* GTM */
import {
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  formatContentData,
} from '../../../../utils/googleTags';

/* Style */
import './ProfilesErrorPage.scss';
import { getLink, Routes } from '../../../../utils/router';
import { useOnBackPress } from '../../../../utils/keyDownEvents';

function ProfilesErrorPage({ realFocusKey, setFocus }) {
  const generalErrorButtonFocusKey = `${realFocusKey}-general-error-button`;
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const location = useLocation();
  const history = useHistory();

  const timeElapsed = Date.now();
  const today = new Date(timeElapsed);
  const todayFormat = today.toISOString();

  const sourceError = location?.state?.source ?? '';
  const platformError = 'WEBOS';
  const locationError = location?.state?.location;
  const typeError = location?.state?.type;
  const isExternCode = !!location?.state?.externCode;
  const externCodeError = isExternCode ? location?.state?.externCode : '';
  const typeErrorLocation = location?.state?.typeError;
  const isParentalControl = !!location?.state?.parentalControl;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('');
  const isParentalControlChanged = (location.state?.changeParentalControl) || false;

  useEffect(() => {
    switch (locationError) {
      case 'PROFILE_CREATE': {
        setErrorTitle(
          <FormattedMessage id="profile.error.title.create" />,
        );
        setErrorMessage(
          <FormattedMessage id="profile.error.subtitle.create" />,
        );
        break;
      }
      case 'PROFILE_DELETE': {
        setErrorTitle(
          <FormattedMessage id="profile.error.title.delete" />,
        );
        setErrorMessage(
          <FormattedMessage id="profile.error.subtitle.delete" />,
        );
        break;
      }
      case 'PROFILE_EDIT': {
        setErrorTitle(
          <FormattedMessage id="profile.error.title.edit" />,
        );
        setErrorMessage(
          <FormattedMessage id="profile.error.subtitle.edit" />,
        );
        break;
      }
      default: {
        break;
      }
    }
  }, [locationError, location]);

  const errorProfileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
        eventName,
        data: {
          errorType: typeError || '-',
          errorCode: externCodeError.toString() || '-',
          content: {
            ...formatContentData({
              rest: { changeParentalControl: isParentalControlChanged },
            }),
          },
        },
      });
    }
  }, [commonTaggingEvent, typeError, externCodeError, isParentalControlChanged]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_CREATE.PAGE_VIEW_ERROR,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      });
      tagsMountRef.current = false;
    }
  }, [errorProfileCommonTagEvent, country]);

  useEffect(() => {
    setFocus(generalErrorButtonFocusKey);
  }, [generalErrorButtonFocusKey, setFocus]);

  const handleBackPress = useCallback(() => {
    history.push(getLink(Routes.Private.Profiles));
  }, [history]);

  const handleClickButton = useCallback(() => {
    if (typeErrorLocation === TYPE_ACTIONS_PROFILE.ONBOARDING) {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.CONTINUE_ERROR_ONBOARDING,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
      });
    } else {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_CREATE.OK_ERROR_CREATE_PROFILE,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      });
    }
    history.push(getLink(Routes.Private.Profiles));
  }, [typeErrorLocation, errorProfileCommonTagEvent, history]);

  useOnBackPress({ listener: handleBackPress });

  return (
    <div className="dtv-html5-c-general-errors">
      <div className="dtv-html5-c-general-errors--center">
        <div className="dtv-html5-c-general-errors--limit">
          <img src={getJsonImage('icon-profile-error') || Icon} alt="dtv Profile Error" className="dtv-html5-c-general-errors--profile_error" />
          <Title
            title={errorTitle || <FormattedMessage id="profile.error.title" />}
            description={errorMessage || <FormattedMessage id="profile.error.subtitle" />}
            descriptionExists
          />

          <div className="dtv-html5-c-general-errors--moreDetails">
            <CodeError
              sourceError={sourceError}
              platformError={platformError}
              locationError={locationError}
              typeError={typeError}
              isExternCode={isExternCode}
              externCode={externCodeError}
            />
          </div>

        </div>
      </div>
      <div className="dtv-html5-c-general-errors--botton">
        <div className="dtv-html5-c-general-errors--buttonAlignment">
          <FocusableButtonprofile
            type="submit"
            secondGray
            bordered
            focused
            focusKey={generalErrorButtonFocusKey}
            onEnterPress={handleClickButton}
          >
            { userProfiles?.length === 0 ? (
              <FormattedMessage id="profile.error.retry" />
            ) : (
              <FormattedMessage id="profile.error.ok" />
            )}
          </FocusableButtonprofile>

        </div>
        {!isParentalControl && (
          <div className="dtv-html5-c-general-errors--info">
            <p>MV010000114200000000000</p>
            <p>{DATADOG_APPLICATION_ID}</p>
              {sourceError === 'MIDDLEWARE' ? (<p>MIDDLEWARE</p>) : ''}
            <p>{todayFormat}</p>
            <p>{APP_VERSION}</p>
          </div>
        )}
      </div>
    </div>
  );
}

ProfilesErrorPage.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(ProfilesErrorPage)));
